import { AnimatePresence, m as motion } from 'framer-motion'
import { AutoAwesome, ExpandMore, Login } from '@mui/icons-material'
import { Box, Button, Stack, Typography } from '@dtx-company/design-system/src'
import { FC, Fragment, ReactNode, useCallback, useState } from 'react'
import { Box as LegacyBox } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { MARKETING_NAV_CONFIG } from '../../../../components/layout/header/data'
import { MarketingPageRoutes, Routes } from '@dtx-company/true-common/src/constants/routes'
import { NavLink, NavSlice } from '@dtx-company/true-common/src/types/appContent'
import { NextLinkWrap } from '@app/page/src/components/util/NextLinkUtils'
import { REDIRECT_TO_CURR_STORE_PAGE_POST_AUTH_ROUTES } from '../../../../components/layout/header/v2/MarketingNavLinks.constants'
import { UnstyledButton } from '../../../../components/layout/header/v2/styles'
import { closeMenuIcon, flowcodeNavLogo } from '@app/common/src/constants/assets'
import { getNavLinkDataTestType } from '../../../../utils/testing'
import { getTopNavDesktopBreakpoint } from '../TopNav.utils'
import { media } from '@app/common/src/theme'
import { setNavLeftMenu } from '@dtx-company/inter-app/src/redux/slices/config-slice'
import { theme } from '@dtx-company/shared-components/src'
import { useDispatch } from 'react-redux'
import { useNavigationRoutes } from '../../../../hooks/useNavigationRoutes'
import { useRouter } from 'next/router'
import Image from 'next/image'
import Link from 'next/link'
import events from '@dtx-company/inter-app/src/event-tracking/events/nav'
import styled from 'styled-components'
import useTranslation from 'next-translate/useTranslation'

export interface MobileNavMenuProps {
  open?: boolean
  items?: MobileNavMenuItemProps[]
  userIsLoggedInOnMobile?: boolean
}

interface MobileNavMenuItemProps {
  header?: string
  subItems?: MobileNavMenuSubItemProps[]
}

interface MobileNavMenuSubItemProps {
  header: string
  subHeader?: string
  link?: string
}

const dropdownItemHidden = 'dropdownItemHidden'
const dropdownItemShown = 'dropdownItemShown'

const dropdownItemVariants = {
  [dropdownItemHidden]: {
    opacity: 0,
    height: '0px',
    transition: { ease: 'easeOut', duration: 0.25, transitionEnd: { display: 'none' } }
  },
  [dropdownItemShown]: {
    height: '100%',
    opacity: 1,
    transition: { duration: 0.25 }
  }
}

const LinkInnerComponent: FC<{
  item: NavLink
  onClick: () => void
}> = ({ item, onClick }) => {
  const dataTestType = getNavLinkDataTestType(item)
  const TextComponent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        py: 4,
        '.MuiTypography-root:first-child': { fontWeight: 600 }
      }}
      role="button"
      onClick={onClick}
    >
      <Typography
        variant="subtitleSmall"
        component="span"
        data-testid={`${item.label}-dropdown-${dataTestType}`}
      >
        {item.label}
      </Typography>
      {item.label_subtext && (
        <Typography
          variant="bodySmall"
          color="primary.grey"
          data-testid={`${item.label_subtext}-dropdown-${dataTestType}-sublabel`}
        >
          {item.label_subtext}
        </Typography>
      )}
    </Box>
  )

  if (item.link && item.isExternal) {
    return (
      <a href={item.link} target="_blank" rel="noopener noreferrer">
        {TextComponent}
      </a>
    )
  } else if (item.link) {
    return (
      <Link href={item.link} data-testid={`${item.label}-dropdown-link-${dataTestType}`}>
        {TextComponent}
      </Link>
    )
  } else {
    return TextComponent
  }
}

interface LinkHeaderProps extends NavSlice {
  open: boolean
}

const LinkHeaderComponent: FC<LinkHeaderProps> = ({ label, link, isExternal, items, open }) => {
  const dataTestType = getNavLinkDataTestType({ label, link, isExternal })

  const LinkText = (
    <Stack
      direction="row"
      alignItems={'center'}
      gap={2}
      sx={{ '.MuiTypography-root': { fontWeight: 600 }, px: 2, py: 3.5 }}
    >
      <Typography
        variant="subtitleMedium"
        component="span"
        data-testid={`${label}-top-level-${dataTestType}`}
      >
        {label}
      </Typography>
      {items?.length ? (
        <Box
          sx={{
            transform: open ? `rotate(-180deg)` : undefined,
            transition: 'all 0.25s ease-in-out'
          }}
          aria-expanded={open}
        >
          <ExpandMore className="expand" />
        </Box>
      ) : null}
    </Stack>
  )
  if (isExternal && link) {
    return (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${label}-top-level-link-${dataTestType}`}
      >
        {LinkText}
      </a>
    )
  } else if (link) {
    return (
      <Link href={link} data-testid={`${label}-top-level-link-${dataTestType}`}>
        {LinkText}
      </Link>
    )
  } else {
    return LinkText
  }
}

const MobileNavMenuItem: FC<NavSlice> = props => {
  const [open, setOpen] = useState(false)
  const dispatch = useDispatch()
  const setMenuClosed = useCallback(() => dispatch(setNavLeftMenu({ open: false })), [dispatch])

  const { items } = props

  return (
    <Box sx={{ flexDirection: 'column' }}>
      <UnstyledButton onClick={() => setOpen(prevState => !prevState)}>
        <LinkHeaderComponent {...props} open={open} />
      </UnstyledButton>
      {items?.length ? (
        <motion.div
          variants={dropdownItemVariants}
          animate={open ? dropdownItemShown : dropdownItemHidden}
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: open ? 'auto' : '0px',
            pointerEvents: open ? 'auto' : 'none',
            padding: '0 24px'
          }}
          data-testid={`${props.label}-dropdown`}
        >
          {items.map((item, idx) => (
            <LinkInnerComponent item={item} onClick={setMenuClosed} key={`${item.label}-${idx}`} />
          ))}
        </motion.div>
      ) : null}
    </Box>
  )
}

export const MobileMarketingNavMenu: FC<MobileNavMenuProps> = props => {
  const { t } = useTranslation('nav')
  const display = props.open ? 'flex' : 'none'
  const dispatch = useDispatch()
  const setMenuClosed = useCallback(() => dispatch(setNavLeftMenu({ open: false })), [dispatch])
  const router = useRouter()

  const redirectToCurrStorePage = REDIRECT_TO_CURR_STORE_PAGE_POST_AUTH_ROUTES.includes(
    router.pathname as Routes
  )
  return (
    <Stack
      direction="column"
      gap={9}
      sx={{
        display: [`${display}`, `${display}`, `${display}`, 'none'],
        flexDirection: 'column',
        height: '100vh',
        overflowY: 'auto',
        p: 4,
        width: '100vw',
        zIndex: 1001
      }}
      data-testid="marketing-nav-mobile"
    >
      <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
        <Image src={flowcodeNavLogo} alt="logo" width={121} height={24} />
        <Image
          role="button"
          src={closeMenuIcon}
          onClick={() => {
            events.Global_Navigation_Closed_MobileNav()
            setMenuClosed()
          }}
          width={21}
          height={21}
          alt=""
        />
      </Stack>
      <Stack gap={8}>
        {props.userIsLoggedInOnMobile ? (
          <Button
            size="large"
            fullWidth
            color="tertiary"
            data-testid="mobile-dashboard-button"
            startIcon={Login}
            href={Routes.ANALYTICS_CODES}
            onClick={() => {
              setMenuClosed()
              events.Global_MarketingNav_Clicked_DashboardButton()
            }}
          >
            {t('Header.v2.Dashboard')}
          </Button>
        ) : (
          <Stack direction="row" gap={2}>
            <NextLinkWrap
              flexShrink={0}
              width="50%"
              data-testid="sign-up"
              href={{
                pathname: Routes.SIGN_UP,
                query: {
                  ...(redirectToCurrStorePage && {
                    redirectTo: encodeURIComponent(router.asPath)
                  })
                }
              }}
            >
              <Button
                size="large"
                fullWidth
                color="tertiary"
                onClick={() => {
                  events.Global_Navigation_MobileNav_Clicked_SignUp()
                  setMenuClosed()
                }}
                data-testid="marketing-nav-mobile-signup"
              >
                {t('Header.v2.SignUpMobile')}
              </Button>
            </NextLinkWrap>

            <NextLinkWrap
              flexShrink={0}
              width="50%"
              data-testid="sign-in"
              href={{
                pathname: Routes.SIGN_IN,
                query: {
                  ...(!redirectToCurrStorePage && { ...router.query }),
                  ...(redirectToCurrStorePage && {
                    redirectTo: encodeURIComponent(router.asPath)
                  })
                }
              }}
            >
              <Button
                fullWidth
                size="large"
                color="secondary"
                onClick={() => {
                  events.Global_Navigation_MobileNav_Clicked_SignIn()
                  setMenuClosed()
                }}
                data-testid="marketing-nav-mobile-signin"
              >
                {t('Header.v2.SignIn')}
              </Button>
            </NextLinkWrap>
          </Stack>
        )}

        <Box>
          {MARKETING_NAV_CONFIG.mobile.map(slice => (
            <Fragment key={slice.label}>
              <MobileNavMenuItem {...slice} />
            </Fragment>
          ))}
        </Box>
        <Button
          fullWidth
          size="large"
          component="a"
          startIcon={AutoAwesome}
          href={MarketingPageRoutes.CONTACT_SALES}
          onClick={() => {
            events.Global_Navigation_MobileNav_Clicked_ConnectWithUs()
            setMenuClosed()
          }}
        >
          {t('Header.v2.BookADemo')}
        </Button>
      </Stack>
    </Stack>
  )
}

const MobileAndTabletWrapper = styled(LegacyBox)<{ hideBreakpoint: number }>`
  ${({ hideBreakpoint }) =>
    `${media.custom(hideBreakpoint)} {
        display: none;
      }
  `}
`

export const MobileLeftNav: FC<MobileNavMenuProps> = props => {
  const { shouldShowPlatformTopNav, userIsLoggedInOnMobile } = useNavigationRoutes()

  const desktopBreakpoint = getTopNavDesktopBreakpoint(shouldShowPlatformTopNav)

  return (
    <MobileAndTabletWrapper hideBreakpoint={desktopBreakpoint}>
      <MobileLeftNavWrapper open={props.open}>
        <MobileMarketingNavMenu {...props} userIsLoggedInOnMobile={userIsLoggedInOnMobile} />
      </MobileLeftNavWrapper>
    </MobileAndTabletWrapper>
  )
}

const MobileLeftNavWrapper: FC<{ open?: boolean; children: ReactNode }> = ({ open, children }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ translateX: '-100vw' }}
        exit={{ translateX: '-100vw' }}
        animate={{ translateX: '0px' }}
        transition={{ duration: 0.25 }}
        style={{
          position: 'fixed',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100001,
          backgroundColor: theme.colors.primary.white,
          width: '100%',
          overflowY: 'hidden',
          maxHeight: '100vh'
        }}
      >
        {children}
      </motion.div>
    )}
  </AnimatePresence>
)
